import { Stack, ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { buildWorkplaceDocumentsPath } from "@src/appV2/redesign/Documents/paths";
import {
  BottomSheet,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import pluralize from "pluralize";
import { useHistory } from "react-router-dom";

export interface PreClockInPostBookingDocumentsBottomSheetProps {
  modalState: UseModalState;
  count: number;
  shiftId: string;
  workerId: string;
  workplaceId: string;
  qualification: string;
}

export function PreClockInPostBookingDocumentsBottomSheet(
  props: PreClockInPostBookingDocumentsBottomSheetProps
) {
  const { modalState, count, shiftId, workerId, workplaceId, qualification } = props;
  const history = useHistory();

  const analyticsPayload = { shiftId, workerId, workplaceId, missingCount: count };

  useLogEffect(
    APP_V2_USER_EVENTS.PRE_CLOCK_IN_POST_BOOKING_DOCUMENTS_SHEET_SHOWN,
    analyticsPayload,
    { enabled: modalState.modalIsOpen }
  );

  const onCtaClick = () => {
    logEvent(APP_V2_USER_EVENTS.PRE_CLOCK_IN_POST_BOOKING_DOCUMENTS_CTA_CLICKED, analyticsPayload);
    modalState.closeModal();
    history.push(
      buildWorkplaceDocumentsPath({
        workplaceId,
        qualification,
        shiftId,
        postBookingRequirementsFilter: "include-only",
      })
    );
  };

  const onSupportClick = () => {
    logEvent(
      APP_V2_USER_EVENTS.PRE_CLOCK_IN_POST_BOOKING_DOCUMENTS_SUPPORT_CLICKED,
      analyticsPayload
    );
    modalState.closeModal();
    void openZendeskMessaging();
  };

  const onDismiss = () => {
    logEvent(
      APP_V2_USER_EVENTS.PRE_CLOCK_IN_POST_BOOKING_DOCUMENTS_SHEET_DISMISSED,
      analyticsPayload
    );
    modalState.closeModal();
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <BottomSheet
        modalState={modalState}
        header={<BottomSheetHeader onClose={onDismiss} />}
        footer={
          <DialogFooter orientation="vertical" sx={{ marginY: 5, gap: 4 }}>
            <Button fullWidth variant="contained" size="large" onClick={onCtaClick}>
              Complete documents
            </Button>
            <Button fullWidth variant="outlined" size="large" onClick={onSupportClick}>
              Contact support
            </Button>
          </DialogFooter>
        }
      >
        <Stack
          spacing={7}
          sx={{ paddingTop: 5, paddingBottom: 5, paddingX: 5, textAlign: "center" }}
        >
          <Title component="h2" sx={{ textWrap: "balance" }}>
            Complete Missing Documents
          </Title>

          <Text semibold variant="body1" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            The workplace requires a bit of paperwork before your shift.{" "}
            <Text inline bold>
              {count} {pluralize("document", count)} to review and sign.
            </Text>{" "}
            Please complete them now to clock-in.
          </Text>
        </Stack>
      </BottomSheet>
    </ThemeProvider>
  );
}
